import React from "react";
import { useTranslation } from "react-i18next";
import { IconBuildingBank, IconCreditCard, IconExternalLink, IconPencil } from "@tabler/icons-react";

import "./styles.scss";

import { Button, Card, StatusChip, Text } from "@fragments";
import banks from "@helpers/banks";
import Parser from "@helpers/parser.js";
import { WALLET_PROVIDERS, walletIsConnected } from "@helpers/wallets";

export default function ConnectedAccount({
    provider,
    wallet,
    loading,
    onRegisterAccount,
    onChangeKey,
}) {
    const { t } = useTranslation("banking");
    const activeProvider = provider || wallet?.provider || WALLET_PROVIDERS.ASAAS;

    return (
        <Card
            flat
            className="withdrawals-connected-account"
            title={t("withdrawals.connectedAccount.title")}
            subtitle={activeProvider === WALLET_PROVIDERS.STRIPE
                ? t("withdrawals.connectedAccount.subtitleStripe")
                : t("withdrawals.connectedAccount.subtitleAsaas")}
            icon={activeProvider === WALLET_PROVIDERS.STRIPE ? <IconCreditCard /> : <IconBuildingBank />}
        >
            {loading ? (
                <Loading />
            ) : activeProvider === WALLET_PROVIDERS.STRIPE ? (
                <StripeAccount wallet={wallet} onRegisterAccount={onRegisterAccount} />
            ) : (
                <AsaasAccount wallet={wallet} onRegisterAccount={onRegisterAccount} onChangeKey={onChangeKey} />
            )}
        </Card>
    );
}

function StripeAccount({ wallet, onRegisterAccount }) {
    const { t } = useTranslation("banking");
    const connected = walletIsConnected(wallet);
    const onboarding = wallet?.onboarding || {};

    return (
        <div className="connected-account-card connected-account-card--stripe">
            <div className="left-section">
                <IconCreditCard className="bank-fallback-icon" />
            </div>

            <div className="right-section">
                <div className="info-row">
                    <Text.label contrast={9}>
                        Stripe Express
                    </Text.label>
                    {connected ? (
                        <StatusChip.Success text={t("withdrawals.connectedAccount.statusActive")} />
                    ) : onboarding.detailsSubmitted ? (
                        <StatusChip.Alert text={t("withdrawals.connectedAccount.statusPending")} />
                    ) : (
                        <StatusChip.Default text={t("withdrawals.connectedAccount.statusNotConnected")} />
                    )}
                </div>

                <Text.span className="info-row" contrast={6}>
                    {connected
                        ? t("withdrawals.connectedAccount.stripeEnabled")
                        : t("withdrawals.connectedAccount.stripeOnboarding")}
                </Text.span>
            </div>

            <Button
                color="overlay"
                size="small"
                startIcon={<IconExternalLink />}
                onClick={onRegisterAccount}
            >
                {connected
                    ? t("withdrawals.connectedAccount.manage")
                    : t("withdrawals.connectedAccount.connect")}
            </Button>
        </div>
    );
}

function AsaasAccount({ wallet, onRegisterAccount, onChangeKey }) {
    const { t } = useTranslation("banking");

    if (!wallet?.withdrawal) {
        return (
            <div className="connected-account-card connected-account-card--empty">
                <div className="right-section">
                    <Text.label contrast={8}>{t("withdrawals.connectedAccount.emptyTitle")}</Text.label>
                    <Text.span contrast={6}>{t("withdrawals.connectedAccount.emptyDescription")}</Text.span>
                </div>
                <Button text={t("withdrawals.connectedAccount.emptyAction")} onClick={onRegisterAccount} size="small" />
            </div>
        );
    }

    const { withdrawal, statuses } = wallet;
    const { receiver } = withdrawal;

    const bankConfig = banks.find(b => parseInt(b.code) === parseInt(receiver?.bank?.code));
    const bankLogo = bankConfig?.logo || receiver?.bank?.logo;

    const statusConfig = {
        'approved': { text: t('withdrawals.connectedAccount.bankStatus.approved'), variant: 'success' },
        'active': { text: t('withdrawals.connectedAccount.bankStatus.active'), variant: 'success' },
        'pending': { text: t('withdrawals.connectedAccount.bankStatus.pending'), variant: 'alert' },
        'rejected': { text: t('withdrawals.connectedAccount.bankStatus.rejected'), variant: 'error' },
        'processing': { text: t('withdrawals.connectedAccount.bankStatus.processing'), variant: 'info' }
    }[statuses?.bank] || { text: statuses?.bank || t('common:status.unknown'), variant: 'default' };

    let keyFormatted = withdrawal.key;
    if (withdrawal.keyType === 'email') {
        keyFormatted = Parser.email(withdrawal.key);
    } else if (withdrawal.keyType === 'phone') {
        keyFormatted = Parser.phone('+55' + withdrawal.key);
    } else if (withdrawal.keyType === 'cpf') {
        keyFormatted = Parser.cpf(withdrawal.key);
    } else if (withdrawal.keyType === 'cnpj') {
        keyFormatted = Parser.cnpj(withdrawal.key);
    }

    return (
        <div className="connected-account-card">
            <div className="left-section">
                {bankLogo ? (
                    <img
                        src={bankLogo}
                        alt={receiver?.bank?.name || t("withdrawals.connectedAccount.bankAlt")}
                        className="bank-logo"
                    />
                ) : (
                    <IconBuildingBank className="bank-fallback-icon" />
                )}
            </div>

            <div className="right-section">
                <div className="info-row">
                    <Text.span contrast={7}>
                        <Text.span weight={500} contrast={7}>{t("withdrawals.connectedAccount.pixKeyLabel")}</Text.span>
                        {keyFormatted}
                    </Text.span>

                    <StatusChip text={statusConfig.text} variant={statusConfig.variant} />

                    {onChangeKey && (
                        <Button
                            className="connected-account-change"
                            variant="text"
                            color="overlay"
                            size="small"
                            startIcon={<IconPencil />}
                            preventLoading
                            onClick={onChangeKey}
                        >
                            {t("withdrawals.connectedAccount.changeKey")}
                        </Button>
                    )}
                </div>

                <Text.span className="info-row" contrast={7}>
                    {receiver?.owner?.name} {receiver?.owner?.document}
                </Text.span>
            </div>
        </div>
    );
}

function Loading() {
    const { t } = useTranslation("banking");

    return (
        <div className="connected-account-card connected-account-card--loading">
            <div className="left-section">
                <IconBuildingBank className="bank-fallback-icon" />
            </div>
            <div className="right-section">
                <Text.label loading contrast={8}>{t("withdrawals.connectedAccount.loadingTitle")}</Text.label>
                <Text.span loading contrast={6}>{t("withdrawals.connectedAccount.loadingSubtitle")}</Text.span>
            </div>
        </div>
    );
}
